import { Helmet } from 'react-helmet-async'

const BASE_URL = 'https://theblackdogjiujitsu.com'

// Business information from data.tsx
const BUSINESS_INFO = {
  name: 'The Black Dog Jiu-Jitsu Company',
  description: 'Brazilian Jiu-Jitsu gym offering adult classes, BJJ 101 beginner programs, and youth programs in Lake Oswego, Oregon.',
  url: BASE_URL,
  telephone: '+1-503-512-9464',
  email: 'info@theblackdogjiujitsu.com',
  address: {
    streetAddress: '25 S. State St., Suite 2115',
    addressLocality: 'Lake Oswego',
    addressRegion: 'OR',
    postalCode: '97034',
    addressCountry: 'US'
  },
  geo: {
    latitude: 45.415230,
    longitude: -122.664397
  },
  image: `${BASE_URL}/og-image.jpg`,
  priceRange: '$$',
  openingHours: [
    'Mo 11:30-13:00',
    'Mo 18:00-21:00',
    'Tu 06:00-07:30',
    'Tu 18:00-21:00',
    'We 11:30-13:00',
    'We 18:00-21:00',
    'Th 06:00-07:30',
    'Th 18:00-21:00',
    'Fr 11:30-13:00',
    'Fr 18:00-20:00',
    'Sa 10:00-12:00'
  ],
  sameAs: [
    'https://www.facebook.com/people/The-Black-Dog-Jiu-Jitsu-Company/100088914739381/',
    'https://www.instagram.com/theblackdogjiujitsu/',
    'https://twitter.com/theblackdogjjco'
  ]
}

/**
 * LocalBusiness Schema - For the gym's local business information
 * Helps Google display business info in search results
 */
export function LocalBusinessSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SportsActivityLocation',
    '@id': `${BASE_URL}/#organization`,
    name: BUSINESS_INFO.name,
    description: BUSINESS_INFO.description,
    url: BUSINESS_INFO.url,
    telephone: BUSINESS_INFO.telephone,
    email: BUSINESS_INFO.email,
    image: BUSINESS_INFO.image,
    priceRange: BUSINESS_INFO.priceRange,
    address: {
      '@type': 'PostalAddress',
      ...BUSINESS_INFO.address
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: BUSINESS_INFO.geo.latitude,
      longitude: BUSINESS_INFO.geo.longitude
    },
    openingHoursSpecification: BUSINESS_INFO.openingHours.map(hours => {
      const [day, time] = hours.split(' ')
      const [opens, closes] = time.split('-')
      const dayMap: Record<string, string> = {
        'Mo': 'Monday', 'Tu': 'Tuesday', 'We': 'Wednesday',
        'Th': 'Thursday', 'Fr': 'Friday', 'Sa': 'Saturday', 'Su': 'Sunday'
      }
      return {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: dayMap[day],
        opens,
        closes
      }
    }),
    sameAs: BUSINESS_INFO.sameAs,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5.0',
      reviewCount: '50',
      bestRating: '5',
      worstRating: '1'
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  )
}

/**
 * Organization Schema - For brand identity
 */
export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${BASE_URL}/#organization`,
    name: BUSINESS_INFO.name,
    url: BUSINESS_INFO.url,
    logo: `${BASE_URL}/logo.png`,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: BUSINESS_INFO.telephone,
      contactType: 'customer service',
      email: BUSINESS_INFO.email,
      availableLanguage: 'English'
    },
    sameAs: BUSINESS_INFO.sameAs
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  )
}

/**
 * BreadcrumbList Schema - For navigation context
 */
interface BreadcrumbItem {
  name: string
  path: string
}

interface BreadcrumbProps {
  items: BreadcrumbItem[]
}

export function BreadcrumbSchema({ items }: BreadcrumbProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.path}`
    }))
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  )
}

/**
 * FAQPage Schema - For FAQ pages to show expandable Q&A in search results
 */
interface FAQItem {
  question: string
  answer: string
}

interface FAQSchemaProps {
  faqs: FAQItem[]
}

export function FAQPageSchema({ faqs }: FAQSchemaProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  )
}

/**
 * WebSite Schema - For site-wide search box potential
 */
export function WebSiteSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${BASE_URL}/#website`,
    url: BASE_URL,
    name: BUSINESS_INFO.name,
    description: BUSINESS_INFO.description,
    publisher: {
      '@id': `${BASE_URL}/#organization`
    }
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  )
}
